<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { DetailPageDismissAction } from '#layers/block-engine/public';
import { useDataModelDisplayNames } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/spaces/public';
import { RenderPageActions, useTruncationTooltip } from '#layers/ui/public';

const {
	collection,
	backRoute: backRouteProp,
} = defineProps<{
	collection: ResolvedMonospaceCollection;
	backRoute?: RouteLocationRaw;
	dismissAction?: DetailPageDismissAction;
}>();

const { t } = useI18n();
const appConfig = useAppConfig();
const { listingRoute } = useCollectionRoutes(() => collection);
const backRoute = computed(() => backRouteProp ?? listingRoute.value);
const { getCollectionDisplayName } = useDataModelDisplayNames();
const backLabel = computed(() => t('common.back_to', { name: getCollectionDisplayName(collection) }));

const backLabelTooltip = useTruncationTooltip(useTemplateRef('backLabelText'));
</script>

<template>
	<DSecondaryHeader bar class="relative ps-3">
		<DTooltip v-if="dismissAction === DetailPageDismissAction.Close" :text="$t('common.close')">
			<DButton
				icon="lucide:x"
				color="neutral"
				variant="ghost"
				size="sm"
				square
				:to="backRoute"
				:aria-label="$t('common.close')"
			/>
		</DTooltip>

		<DTooltip v-else v-bind="backLabelTooltip" :text="backLabel">
			<DButton
				:icon="appConfig.ui.icons.back"
				color="neutral"
				variant="ghost"
				size="sm"
				:to="backRoute"
				class="min-w-0"
			>
				<span ref="backLabelText" class="truncate">{{ backLabel }}</span>
			</DButton>
		</DTooltip>
		<template #actions>
			<RenderPageActions />
		</template>
	</DSecondaryHeader>
</template>
